import { World } from './world'
import { Renderer } from './renderer'
import type { Organism } from './organism'
import type { PopulationConfig } from './types'

const canvas = document.getElementById('canvas') as HTMLCanvasElement
let world: World
let renderer: Renderer
let lastTime = 0
let fps = 60
let running = false
let speedMultiplier = 1
let selectedOrganism: Organism | null = null

// バージョン情報
declare const __COMMIT_HASH__: string
declare const __BUILD_DATE__: string
declare const __APP_VERSION__: string
const versionEl = document.getElementById('version-info')
if (versionEl) {
  versionEl.textContent = `${__APP_VERSION__} (${__COMMIT_HASH__} / ${__BUILD_DATE__})`
}

// 設定UI要素
const setupOverlay = document.getElementById('setup-overlay')!
const sliderHerb = document.getElementById('slider-herb') as HTMLInputElement
const sliderCarn = document.getElementById('slider-carn') as HTMLInputElement
const sliderOmni = document.getElementById('slider-omni') as HTMLInputElement
const valHerb = document.getElementById('val-herb')!
const valCarn = document.getElementById('val-carn')!
const valOmni = document.getElementById('val-omni')!
const valTotal = document.getElementById('val-total')!

function updateSetupLabels(): void {
  valHerb.textContent = sliderHerb.value
  valCarn.textContent = sliderCarn.value
  valOmni.textContent = sliderOmni.value
  valTotal.textContent = String(
    parseInt(sliderHerb.value) + parseInt(sliderCarn.value) + parseInt(sliderOmni.value)
  )
}
sliderHerb.addEventListener('input', updateSetupLabels)
sliderCarn.addEventListener('input', updateSetupLabels)
sliderOmni.addEventListener('input', updateSetupLabels)

function getPopConfig(): PopulationConfig {
  return {
    herbivores: parseInt(sliderHerb.value),
    carnivores: parseInt(sliderCarn.value),
    omnivores: parseInt(sliderOmni.value),
  }
}

function showSetup(): void {
  running = false
  setupOverlay.classList.remove('hidden')
}

function getAdBannerHeight(): number {
  const banner = document.getElementById('ad-banner')
  if (!banner || banner.classList.contains('hidden')) return 0
  return banner.offsetHeight
}

function resize(): void {
  const bannerH = getAdBannerHeight()
  canvas.style.top = bannerH + 'px'
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight - bannerH
  if (renderer) renderer.resize(canvas.width, canvas.height)
}

function startSimulation(popConfig: PopulationConfig): void {
  resize()
  world = new World(canvas.width, canvas.height, popConfig)
  renderer = new Renderer(canvas)
  selectedOrganism = null
  setupOverlay.classList.add('hidden')
  running = true
  const btn = document.getElementById('btn-pause')
  if (btn) btn.textContent = '\u23f8 \u4e00\u6642\u505c\u6b62'
  lastTime = performance.now()
  requestAnimationFrame(loop)
}

document.getElementById('btn-start')?.addEventListener('click', () => {
  startSimulation(getPopConfig())
})

function loop(now: number): void {
  if (!running) return
  requestAnimationFrame(loop)

  const rawDt = Math.min((now - lastTime) / 1000, 0.05)
  lastTime = now

  fps = fps * 0.9 + (1 / (rawDt || 0.016)) * 0.1

  const dt = rawDt * speedMultiplier
  world.update(dt)

  if (selectedOrganism && !world.organisms.find(o => o.id === selectedOrganism!.id)) {
    selectedOrganism = null
  }

  renderer.render(world, selectedOrganism)
  updateStatsPanel()
  updateDetailPanel()
}

function updateStatsPanel(): void {
  const s = world.stats
  const el = document.getElementById('stats-panel')
  if (!el) return
  el.innerHTML = `
    <div class="stat-row"><span class="label">FPS</span><span class="value">${fps.toFixed(0)}</span></div>
    <div class="stat-row"><span class="label">Tick</span><span class="value">${s.tick}</span></div>
    <div class="stat-row"><span class="label">\u6700\u5927\u4e16\u4ee3</span><span class="value">${s.maxGeneration}</span></div>
    <hr class="stat-divider">
    <div class="stat-row"><span class="label">\u7dcf\u500b\u4f53\u6570</span><span class="value">${s.organisms}</span></div>
    <div class="stat-row"><span class="label herb">\u8349\u98df</span><span class="value">${s.herbivores}</span></div>
    <div class="stat-row"><span class="label carn">\u8089\u98df</span><span class="value">${s.carnivores}</span></div>
    <div class="stat-row"><span class="label omni">\u96d1\u98df</span><span class="value">${s.omnivores}</span></div>
    <div class="stat-row"><span class="label">\u98df\u6599\u6570</span><span class="value">${s.foods}</span></div>
    <div class="stat-row"><span class="label">\u571f\u58cc\u80a5\u6c83\u5ea6</span><span class="value">${(s.soilFertility * 100).toFixed(0)}%</span></div>
  `
}

function updateDetailPanel(): void {
  const panel = document.getElementById('detail-panel')
  if (!panel) return
  if (!selectedOrganism) {
    panel.style.display = 'none'
    return
  }
  const o = world.organisms.find(x => x.id === selectedOrganism!.id)
  if (!o) {
    panel.style.display = 'none'
    selectedOrganism = null
    return
  }
  const t = o.traits
  const herbPct = (t.dietScore * 100).toFixed(0)
  const carnPct = ((1 - t.dietScore) * 100).toFixed(0)
  const dietLabel = t.dietScore >= 0.7 ? '\u8349\u98df\u50be\u5411' : t.dietScore <= 0.3 ? '\u8089\u98df\u50be\u5411' : '\u96d1\u98df\u50be\u5411'
  panel.style.display = 'block'
  panel.innerHTML = `
    <div class="detail-title">\u500b\u4f53 #${o.id}</div>
    <div class="stat-row"><span class="label">\u98df\u6027</span><span class="value">${dietLabel}</span></div>
    <div class="stat-row"><span class="label herb">\u8349\u98df\u5ea6</span><span class="value">${herbPct}%</span></div>
    <div class="stat-row"><span class="label carn">\u8089\u98df\u5ea6</span><span class="value">${carnPct}%</span></div>
    <div class="stat-row"><span class="label">\u4e16\u4ee3</span><span class="value">${o.generation}</span></div>
    <div class="stat-row"><span class="label">\u89aa ID</span><span class="value">${o.parentId ?? '\u2014'}</span></div>
    <div class="stat-row"><span class="label">\u30a8\u30cd\u30eb\u30ae\u30fc</span><span class="value">${o.energy.toFixed(1)}</span></div>
    <div class="stat-row"><span class="label">\u5e74\u9f62</span><span class="value">${o.age.toFixed(1)}s / ${o.maxLifespan.toFixed(0)}s</span></div>
    <hr class="stat-divider">
    <div class="stat-row"><span class="label">\u901f\u5ea6</span><span class="value">${t.speed.toFixed(2)}</span></div>
    <div class="stat-row"><span class="label">\u8996\u91ce</span><span class="value">${t.visionRange.toFixed(1)}</span></div>
    <div class="stat-row"><span class="label">\u30b5\u30a4\u30ba</span><span class="value">${t.size.toFixed(2)}</span></div>
    <div class="stat-row"><span class="label">\u4ee3\u8b1d</span><span class="value">${t.metabolismRate.toFixed(2)}</span></div>
    <button id="btn-deselect" style="margin-top:8px;width:100%">\u2715 \u9078\u629e\u89e3\u9664</button>
  `
  document.getElementById('btn-deselect')?.addEventListener('click', () => {
    selectedOrganism = null
  })
}

canvas.addEventListener('click', (e) => {
  const rect = canvas.getBoundingClientRect()
  const mx = e.clientX - rect.left
  const my = e.clientY - rect.top

  let closest: Organism | null = null
  let closestDist = 20

  for (const org of world.organisms) {
    const dx = org.pos.x - mx
    const dy = org.pos.y - my
    const d = Math.sqrt(dx * dx + dy * dy)
    if (d < closestDist) {
      closestDist = d
      closest = org
    }
  }

  selectedOrganism = closest
})

document.getElementById('btn-pause')?.addEventListener('click', () => {
  running = !running
  const btn = document.getElementById('btn-pause')!
  btn.textContent = running ? '\u23f8 \u4e00\u6642\u505c\u6b62' : '\u25b6 \u518d\u958b'
  if (running) {
    lastTime = performance.now()
    requestAnimationFrame(loop)
  }
})

document.getElementById('btn-reset')?.addEventListener('click', () => {
  showSetup()
})

const speedSlider = document.getElementById('speed-slider') as HTMLInputElement | null
const speedLabel = document.getElementById('speed-label')
speedSlider?.addEventListener('input', () => {
  speedMultiplier = parseFloat(speedSlider.value)
  if (speedLabel) speedLabel.textContent = `\u00d7${speedMultiplier.toFixed(1)}`
})

window.addEventListener('resize', () => {
  resize()
  if (running && world) {
    world = new World(canvas.width, canvas.height, getPopConfig())
    selectedOrganism = null
  }
})

resize()
